import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { useEffect, useRef, useState } from 'react';

export default function useEventSharingSettings(activationId: string) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastChangesMade, setLastChangesMade] = useState<Date | null>(null);

    const [shareViaText, setShareViaText] = useState(true);
    const [shareViaEmail, setShareViaEmail] = useState(true);
    const [customMessage, setCustomMessage] = useState('');
    const [galleryLogo, setGalleryLogo] = useState<string | null>(null);
    const [foregroundColor, setForegroundColor] = useState<string>('#000000');
    const [backgroundColor, setBackgroundColor] = useState<string>('#FFFFFF');
    const [headerForegroundColor, setHeaderForegroundColor] =
        useState<string>('#FFFFFF');
    const [headerBackgroundColor, setHeaderBackgroundColor] =
        useState<string>('#000000');

    const fetchEventDetails = async () => {
        try {
            setIsLoading(true);
            const response = await authenticatedApi.get(
                `/${orgId}/activations/v2/${activationId}/shareAndGallerySettings`,
            );
            const data = response.data;
            const { gallerySettings, sharing } = data;

            setShareViaText(sharing.shareViaText);
            setShareViaEmail(sharing.shareViaEmail);
            setCustomMessage(sharing.customMessage);
            setGalleryLogo(gallerySettings.logo);
            setForegroundColor(gallerySettings.foregroundColor);
            setBackgroundColor(gallerySettings.backgroundColor);
            setHeaderForegroundColor(gallerySettings.headerForegroundColor);
            setHeaderBackgroundColor(gallerySettings.headerBackgroundColor);
        } catch (error) {
            setError('Error fetching event details');
            console.error('Error fetching event details', error);
        } finally {
            setIsLoading(false);
        }
    };

    useUpdateEffect(() => {
        setLastChangesMade(new Date());
    }, [
        shareViaText,
        shareViaEmail,
        customMessage,
        galleryLogo,
        foregroundColor,
        backgroundColor,
        headerForegroundColor,
        headerBackgroundColor,
    ]);

    useEffect(() => {
        fetchEventDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const saveEventDetails = async () => {
        console.log('Saving event details');

        if (!foregroundColor || !backgroundColor) {
            return;
        }

        setIsSaving(true);
        try {
            await authenticatedApi.put(
                `/${orgId}/activations/v2/${activationId}/shareAndGallerySettings`,
                {
                    sharing: {
                        shareViaText,
                        shareViaEmail,
                        customMessage,
                    },
                    gallerySettings: {
                        logo: galleryLogo,
                        foregroundColor,
                        backgroundColor,
                        headerForegroundColor,
                        headerBackgroundColor,
                    },
                },
            );
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving event details', error);
            setError('Error saving event details');
        } finally {
            setIsSaving(false);
        }
    };

    useDebouncedEffect(
        saveEventDetails,
        [
            shareViaText,
            shareViaEmail,
            customMessage,
            galleryLogo,
            foregroundColor,
            backgroundColor,
            headerForegroundColor,
            headerBackgroundColor,
        ],
        2000,
    );

    return {
        shareViaText,
        shareViaEmail,
        customMessage,
        galleryLogo,
        foregroundColor,
        backgroundColor,
        headerForegroundColor,
        headerBackgroundColor,
        setShareViaText,
        setShareViaEmail,
        setCustomMessage,
        setGalleryLogo,
        setForegroundColor,
        setBackgroundColor,
        setHeaderForegroundColor,
        setHeaderBackgroundColor,
        isSaving,
        lastSaved,
        isLoading,
        error,
        lastChangesMade,
    };
}

function useDebouncedEffect(fn: () => void, deps: any[], delay: number) {
    useEffect(() => {
        const handler = setTimeout(() => fn(), delay);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}

function useUpdateEffect(effect: () => void, deps: any[]) {
    const firstRenderRef = useRef(true);

    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        effect();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
